<template>
	<p-message v-if="!query.account_manager_id" :closable="false"
		>Select a sales associate and parent account to begin...</p-message
	>
	<query-form v-model:query="query" :loading="loading" @apply="getReportData" @abort="clearQuery">
		<template #display>
			<div class="display-value">
				<div class="label">Date Range:</div>
				<div class="value">{{ formatDate(query.date_range, 'ddd, MMM D, YYYY') }}</div>
			</div>
			<div class="display-value">
				<div class="label">Sales Associate:</div>
				<div class="value">{{ displayLabel(query.account_manager_id, account_manager_options) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Parent Account:</div>
				<div class="value">{{ displayLabel(query.parent_account_id, parent_account_options) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Vertical:</div>
				<div class="value">{{ displaySelected(query.filters.vertical_id, $root.appStore.verticalOptions) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Grouping:</div>
				<div class="value">{{ displayLabel(query.grouping, grouping_options) }}</div>
			</div>
		</template>
		<template #utils>
			<p-button
				v-tooltip.top="'Refresh'"
				icon="pi pi-refresh"
				aria-label="Refresh"
				class="mr-2"
				@click="getReportData(query, true)"
			/>
		</template>
		<template #form="form">
			<row>
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Date Range:</label>
							<div class="controls">
								<div class="field">
									<date-range-picker v-model="form.query.date_range" :date-options="date_picker_options" />
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Vertical:</label>
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.vertical_id"
										:options="$root.appStore.verticalOptions"
										option-label="label"
										option-value="value"
										placeholder="Filter by Vertical"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Sales Associate:</label>
							<div class="controls">
								<div class="field">
									<p-dropdown
										v-model="form.query.account_manager_id"
										:options="account_manager_options"
										option-label="label"
										option-value="value"
										placeholder="Select Sales Associate"
									/>
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Parent Account:</label>
							<div class="controls">
								<div class="field">
									<p-dropdown
										v-model="form.query.parent_account_id"
										:options="parent_account_options"
										option-label="label"
										option-value="value"
										placeholder="Select Sales Associate First"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Grouping:</label>
							<div class="controls">
								<div class="field">
									<radio-group v-model="form.query.grouping" :options="grouping_options" stacked />
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column></column>
			</row>
		</template>
	</query-form>
	<gutter size="10px" />
	<h2 v-if="query.parent_account_id">
		<router-link :to="parentAccountLink(query.parent_account_id)">{{
			displayLabel(query.parent_account_id, parent_account_options)
		}}</router-link>
		<div class="sub-value">{{ displayLabel(query.account_manager_id, account_manager_options) }}</div>
	</h2>
	<div class="card padded table-card">
		<div class="data-table">
			<p-data-table
				:value="report_data"
				ref="sa_performance_table"
				data-key="date"
				class="sa-performance-table"
				scrollable
				scroll-height="calc(100vh - 450px)"
				:loading="loading"
			>
				<template #empty>
					<div class="dim">No Data</div>
				</template>
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<p-column header="Date" sortable field="grouping">
					<template #body="row">
						{{
							query.grouping === 'month'
								? formatDate(row.data.grouping, 'MMM YYYY')
								: formatDate(row.data.grouping, 'MMM D, YYYY')
						}}
					</template>
				</p-column>
				<p-column header="Active Accounts" sortable field="active_accounts" class="column-align-right shaded">
					<template #body="row">
						<div :class="cellClass(row.data.active_accounts, 'active_accounts')">
							{{ row.data.active_accounts }}<br />
							<div class="sub-value" v-html="trend(row.data.active_accounts_velocity)" />
						</div>
					</template>
				</p-column>
				<p-column header="Data Spend" sortable field="data_spend" class="column-align-right shaded">
					<template #body="row">
						<div :class="cellClass(row.data.data_spend, 'data_spend')">
							{{ currency(row.data.data_spend) }}<br />
							<div class="sub-value" v-html="trend(row.data.data_spend_velocity)" />
						</div>
					</template>
				</p-column>
				<p-column header="Call Spend" sortable field="call_spend" class="column-align-right shaded">
					<template #body="row">
						<div :class="cellClass(row.data.call_spend, 'call_spend')">
							{{ currency(row.data.call_spend) }}<br />
							<div class="sub-value" v-html="trend(row.data.call_spend_velocity)" />
						</div>
					</template>
				</p-column>
				<p-column header="LT Spend" sortable field="lt_spend" class="column-align-right shaded">
					<template #body="row">
						<div :class="cellClass(row.data.lt_spend, 'lt_spend')">
							{{ currency(row.data.lt_spend) }}<br />
							<div class="sub-value" v-html="trend(row.data.lt_spend_velocity)" />
						</div>
					</template>
				</p-column>
				<p-column header="Total Spend" sortable field="total_spend" class="column-align-right shaded">
					<template #body="row">
						<div :class="cellClass(row.data.total_spend, 'total_spend')">
							{{ currency(row.data.total_spend) }}<br />
							<div class="sub-value" v-html="trend(row.data.total_spend_velocity)" />
						</div>
					</template>
				</p-column>
				<p-column header="Spend/Account" sortable field="spend_per_account" class="column-align-right shaded">
					<template #body="row">
						<div :class="cellClass(row.data.spend_per_account, 'spend_per_account')">
							{{ currency(row.data.spend_per_account) }}<br />
							<div class="sub-value" v-html="trend(row.data.spend_per_account_velocity)" />
						</div>
					</template>
				</p-column>
			</p-data-table>
		</div>
	</div>
</template>

<script>
import pCalendar from 'primevue/calendar';
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import lineLoader from '@/components/elements/LineLoader.vue';
import radioGroup from '@/components/forms/RadioGroup.vue';
import queryForm from '@/components/widgets/QueryForm.vue';
import quickFilter from '@/components/widgets/QuickFilter.vue';
import {
	colorCell,
	currency,
	displayLabel,
	displaySelected,
	formatDate,
	percentage,
	title,
	trend,
} from '@/lib/Filters';
import { getAccountManagersAsOptions, getParentAccountsByAM, reportSAPerformance } from '@GQL';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import { cloneDeep, get, max, min, round } from 'lodash-es';

dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

export default {
	name: 'SAPerformanceTable',
	components: {
		dateRangePicker,
		lineLoader,
		pCalendar,
		queryForm,
		quickFilter,
		radioGroup,
	},
	data() {
		return {
			loading: false,
			query: {
				date_range: [dayjs().subtract(3, 'weeks').startOf('isoWeek').toDate(), dayjs().endOf('isoWeek').toDate()],
				grouping: 'day',
				account_manager_id: null,
				parent_account_id: null,
				filters: {
					product: [],
					campaign_type: [],
					vertical_id: [],
				},
			},
			report_data: [],
			account_manager_options: [
				{
					label: 'None Selected',
					value: null,
				},
			],
			parent_account_options: [
				{
					label: 'None Selected',
					value: null,
				},
			],
			grouping_options: [
				{ label: 'Day', value: 'day' },
				{ label: 'Week', value: 'week' },
				{ label: 'Month', value: 'month' },
			],
			campaign_type_options: [
				{ label: 'Exclusive Data', value: 'exclusive_data' },
				{ label: 'Shared Data', value: 'shared_data' },
				{ label: 'Premium Listing', value: 'premium_listing' },
				{ label: 'Call', value: 'call' },
				{ label: 'Live Transfer', value: 'live_transfer' },
			],
			date_picker_options: [
				{
					label: 'This Week',
					icon: 'calendar-today',
					value: [dayjs().startOf('isoWeek').toDate(), dayjs().endOf('isoWeek').toDate()],
				},
				{
					label: 'This Month',
					icon: 'calendar-today',
					value: [dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()],
				},
				{
					label: 'Last Month',
					icon: 'calendar-today',
					value: [
						dayjs().subtract(1, 'month').startOf('month').toDate(),
						dayjs().subtract(1, 'month').endOf('month').toDate(),
					],
				},
				{
					label: 'Last 4 Weeks',
					icon: 'calendar-today',
					value: [dayjs().subtract(3, 'weeks').startOf('isoWeek').toDate(), dayjs().endOf('isoWeek').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last 6 Months',
					value: [dayjs().subtract(5, 'months').startOf('month').toDate(), dayjs().endOf('month').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last 12 Months',
					value: [dayjs().subtract(11, 'months').startOf('month').toDate(), dayjs().endOf('month').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'This Calendar Year',
					value: [dayjs().startOf('year').toDate(), dayjs().endOf('year').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last Calendar Year',
					value: [
						dayjs().startOf('year').subtract(1, 'year').toDate(),
						dayjs().subtract(1, 'year').endOf('year').toDate(),
					],
				},
			],
		};
	},
	computed: {
		selectedAccountManagerId() {
			return this.query.account_manager_id;
		},
	},
	watch: {
		async selectedAccountManagerId(new_value) {
			await this.getParentAccountIds(new_value);
		},
	},
	methods: {
		colorCell,
		currency,
		displayLabel,
		displaySelected,
		formatDate,
		percentage,
		round,
		title,
		trend,
		parentAccountLink(parent_account_id) {
			return {
				name: 'ParentAccountsDetail:ParentAccountTracker',
				params: {
					mpid: parent_account_id.substring(0, 3),
					parent_account_id,
				},
			};
		},
		async getReportData(query, refresh) {
			const report_query = cloneDeep(query);
			report_query.month = dayjs(report_query.month).format('YYYY-MM');

			try {
				this.loading = true;
				this.report_data = await reportSAPerformance(report_query, refresh);
			} finally {
				this.loading = false;
			}

			// Scroll all the way right
			if (this.report_data.length) {
				this.$nextTick(() => {
					document.querySelector('.p-datatable-wrapper').scrollLeft =
						document.querySelector('.p-datatable-wrapper').scrollWidth;
				});
			}
		},
		async getParentAccountIds(account_manager_id) {
			if (account_manager_id) {
				const parent_accounts = await getParentAccountsByAM(account_manager_id);
				if (parent_accounts) {
					this.parent_account_options = parent_accounts.map((account) => {
						return {
							label: account.name,
							value: account.id,
						};
					});
				}
			}
		},
		clearQuery() {
			this.loading = false;
		},
		cellClass(value, column) {
			const min_value = min(
				this.report_data.map((row) => {
					return get(row, column, 0);
				})
			);
			const max_value = max(
				this.report_data.map((row) => {
					return get(row, column, 0);
				})
			);
			return colorCell(value, min_value, max_value) || 'color-cell';
		},
	},
	async mounted() {
		const account_managers = await getAccountManagersAsOptions(['sales_associate']);
		if (account_managers[0]) {
			this.account_manager_options = account_managers[0].items;
			this.account_manager_options.unshift({
				label: 'None Selected',
				value: null,
			});
		}

		// Check if the current user is an SA
		const selected_am = this.account_manager_options.find((option) => {
			return option.value === this.$root.sessionStore.user.id;
		});
		if (selected_am) {
			this.query.account_manager_id = this.$root.sessionStore.user.id;
			await this.getReportData(this.query, true);
		}
	},
};
</script>

<style lang="less" scoped>
.table-card {
	max-height: calc(100% - 195px);
}

:deep(.p-datatable-wrapper) {
	height: 100%;
	max-height: 100%;
	overflow: auto;
	overscroll-behavior-x: none !important;
}

:deep(.p-datatable-thead tr:first-child th) {
	border-bottom: 1px solid var(--gray-20);
}

:deep(.p-datatable-tbody) {
	overflow-y: auto;
}

:deep(td.shaded) {
	padding: 0 !important;
}

:deep(.color-cell) {
	padding: calc(0.75rem - 1px) 1rem;

	.sub-value {
		background-color: #fff;
		border-radius: 3px;
		display: inline-block;
		padding: 0 3px;
	}
}
</style>
